// The data of the Voice page of one assistant: the state of the prompt (what can be edited, the compiled prompt,
// where a publication goes), the versions, the audit log and the drift against the voice provider — and the four
// things the page does with them (save, publish, restore, check the drift).
//
//   const voice = useVoicePrompt(() => assistantId.value);
//
// Each action rejects with the request error, so the page decides what to say. The drift is asked on its own and
// never blocks the page: the provider may take seconds to answer, or not answer at all.
import { reactive, ref, shallowRef, toValue } from 'vue';
import VoicePromptAPI from 'dashboard/api/polaris/voicePrompt';

const emptyList = () => ({ items: [], total: 0, page: 1 });

export function useVoicePrompt(assistantId) {
  const state = shallowRef(null);
  const drift = shallowRef(null);
  const versions = ref(emptyList());
  const auditEvents = ref(emptyList());
  const flags = reactive({
    fetching: false,
    saving: false,
    publishing: false,
    checkingDrift: false,
  });

  const id = () => toValue(assistantId);
  const toList = ({ data }) => ({
    items: data.payload,
    total: data.meta.total_count,
    page: data.meta.page,
  });

  const loadVersions = async (page = 1) => {
    versions.value = toList(await VoicePromptAPI.versions(id(), page));
  };

  const loadAuditEvents = async (page = 1) => {
    auditEvents.value = toList(await VoicePromptAPI.auditEvents(id(), page));
  };

  const checkDrift = async () => {
    const asked = id();
    flags.checkingDrift = true;
    try {
      const { data } = await VoicePromptAPI.drift(asked);
      if (asked === id()) drift.value = data;
    } catch {
      if (asked === id()) drift.value = { state: 'unavailable', targets: [] };
    } finally {
      flags.checkingDrift = false;
    }
    // A drift found now is written to the log by the server: show it.
    if (drift.value?.state === 'drifted') await loadAuditEvents();
  };

  const loadState = async () => {
    const { data } = await VoicePromptAPI.show(id());
    state.value = data;
  };

  const refresh = () =>
    Promise.all([loadState(), loadVersions(), loadAuditEvents()]);

  const load = async () => {
    state.value = null;
    drift.value = null;
    versions.value = emptyList();
    auditEvents.value = emptyList();
    flags.fetching = true;
    try {
      await refresh();
    } finally {
      flags.fetching = false;
    }
    checkDrift();
  };

  const save = async ({ promptBlocks, voice }) => {
    flags.saving = true;
    try {
      const { data } = await VoicePromptAPI.update(id(), {
        promptBlocks,
        voice,
      });
      state.value = data;
      await loadAuditEvents();
    } finally {
      flags.saving = false;
    }
    checkDrift();
  };

  // Publishing and restoring both answer with the new version; the lists and the state are read again after.
  const withNewVersion = async request => {
    flags.publishing = true;
    try {
      const { data } = await request();
      await refresh();
      checkDrift();
      return data;
    } finally {
      flags.publishing = false;
    }
  };

  const publish = reason =>
    withNewVersion(() => VoicePromptAPI.publish(id(), reason));

  const restore = (versionNo, reason) =>
    withNewVersion(() => VoicePromptAPI.restore(id(), versionNo, reason));

  return {
    state,
    drift,
    versions,
    auditEvents,
    flags,
    load,
    save,
    publish,
    restore,
    checkDrift,
    loadVersions,
    loadAuditEvents,
  };
}
